import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function startOfWeek(d = new Date()) {
  const day = (d.getDay() + 6) % 7; // Monday = 0
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - day);
  return s;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// Build a YYYY-MM-DD key from a Date's LOCAL components. Critical: we must
// never use `toISOString()` here — that converts to UTC and, in any timezone
// east of GMT (e.g. Lisbon UTC+1 in summer), shifts midnight-anchored dates
// back by one day, which previously caused the "today" highlight and event
// bucketing to land on the wrong column.
function localDayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Calendar() {
  const { t, lang } = useLang();
  const [weekStart, setWeekStart] = useState(startOfWeek());
  const [events, setEvents] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState("");

  const weekEnd = addDays(weekStart, 7);

  const load = useCallback(async () => {
    const from_iso = weekStart.toISOString();
    const to_iso = weekEnd.toISOString();
    const { data } = await api.get("/events", { params: { from_iso, to_iso } });
    setEvents(data.events);
  }, [weekStart, weekEnd]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const dayKey = (d) => localDayKey(d);
  const eventsByDay = useMemo(() => {
    const acc = days.reduce((a, d) => ({ ...a, [dayKey(d)]: [] }), {});
    events.forEach((ev) => {
      // ev.start arrives as an ISO string in UTC ("…Z" or with an offset).
      // Parse it into a Date and bucket by its LOCAL day, not by the raw
      // UTC slice — otherwise events near local midnight cross the day.
      const evDate = new Date(ev.start);
      if (Number.isNaN(evDate.getTime())) return;
      const k = localDayKey(evDate);
      if (acc[k]) acc[k].push(ev);
    });
    return acc;
  }, [days, events]);

  const fmtDay = (d) =>
    d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { weekday: "short", day: "numeric", month: "short" });

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString(lang === "pt" ? "pt-PT" : "en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "?";
    }
  };

  const onDelete = async (id) => {
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-cap">— {t("app.sidebar2.calendar")}</p>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="calendar-title">
            {t("app.calendarTitle")}
          </h1>
          <p className="font-display-italic text-[var(--ink-soft)] mt-3 max-w-3xl">
            {t("app.calendarSub")}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => setShowNew(true)} className="btn-outline" data-testid="calendar-add-btn">
            + {t("app.calendarAdd")}
          </button>
          <a
            href={`${BACKEND_URL}/api/events/ical`}
            target="_blank"
            rel="noreferrer"
            className="btn-ghost"
            data-testid="calendar-ical-btn"
          >
            ⤓ {t("app.calendarIcal")}
          </a>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="btn-ghost" data-testid="calendar-prev-btn">
          ← {t("app.calendarPrev")}
        </button>
        <button onClick={() => setWeekStart(startOfWeek())} className="btn-ghost" data-testid="calendar-today-btn">
          ● {t("app.calendarToday")}
        </button>
        <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="btn-ghost" data-testid="calendar-next-btn">
          {t("app.calendarNext")} →
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-7 gap-px bg-[var(--border)]" data-testid="calendar-grid">
        {days.map((d) => {
          const k = dayKey(d);
          const list = eventsByDay[k];
          const isToday = k === localDayKey(new Date());
          return (
            <div key={k} className={`bg-[var(--bg)] min-h-[180px] p-4 ${isToday ? "border-t-2 border-[var(--oxblood)]" : ""}`}>
              <p className={`label-cap ${isToday ? "text-[var(--oxblood)]" : "text-[var(--ink-soft)]"}`}>
                {fmtDay(d)}
              </p>
              <div className="mt-3 space-y-2">
                {list.length === 0 && (
                  <p className="text-xs text-[var(--ink-faint)] italic">—</p>
                )}
                {list.map((ev) => (
                  <div key={ev.id} className="border-l-2 border-[var(--oxblood)] pl-2 py-1 group">
                    <p className="text-[11px] text-[var(--ink-soft)] tracking-monocle uppercase">
                      {fmtTime(ev.start)}
                    </p>
                    <p className="font-display text-sm leading-snug">{ev.title}</p>
                    {ev.location && <p className="text-xs text-[var(--ink-faint)]">{ev.location}</p>}
                    <button
                      onClick={() => onDelete(ev.id)}
                      className="text-xs text-[var(--ink-faint)] hover:text-[var(--error)] opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`event-delete-${ev.id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {events.length === 0 && (
        <p className="mt-10 font-display-italic text-[var(--ink-soft)] text-center" data-testid="calendar-no-events">
          {t("app.calendarNoEvents")}
        </p>
      )}

      {showNew && <NewEventDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewEventDialog({ onClose, onCreated }) {
  const { t } = useLang();
  const now = new Date();
  now.setMinutes(0, 0, 0);
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(now.toISOString().slice(0, 16));
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        title,
        start: new Date(start).toISOString(),
        end: end ? new Date(end).toISOString() : null,
        location,
        notes,
      };
      const { data } = await api.post("/events", payload);
      if (data.conflicts && data.conflicts.length > 0) {
        setConflicts(data.conflicts);
        // still keep the event; let user see
      }
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-[var(--ink)]/40 flex items-center justify-center px-4" onClick={onClose} data-testid="new-event-dialog">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[var(--surface)] border border-[var(--border)] p-10 w-full max-w-lg"
      >
        <p className="label-cap">— {t("app.calendarAdd")}</p>
        <h2 className="font-display text-3xl font-light tracking-tight mt-4">{t("app.eventTitle")}</h2>
        <div className="mt-6 space-y-5">
          <label className="block">
            <span className="label-cap">{t("app.eventTitle")}</span>
            <input className="line-input" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-event-title" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="label-cap">{t("app.eventStart")}</span>
              <input type="datetime-local" className="line-input" required value={start} onChange={(e) => setStart(e.target.value)} data-testid="new-event-start" />
            </label>
            <label className="block">
              <span className="label-cap">{t("app.eventEnd")}</span>
              <input type="datetime-local" className="line-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="new-event-end" />
            </label>
          </div>
          <label className="block">
            <span className="label-cap">{t("app.eventLocation")}</span>
            <input className="line-input" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="new-event-location" />
          </label>
          <label className="block">
            <span className="label-cap">{t("app.eventNotes")}</span>
            <input className="line-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="new-event-notes" />
          </label>
        </div>
        {conflicts.length > 0 && (
          <div className="mt-4 border-l-2 border-[var(--oxblood)] pl-3">
            <p className="label-cap text-[var(--oxblood)]">{t("app.calendarConflicts")}</p>
            {conflicts.map((c) => (
              <p key={c.id} className="text-sm">{c.title}</p>
            ))}
          </div>
        )}
        {error && <p className="text-[var(--error)] text-sm mt-3">{error}</p>}
        <div className="flex justify-end gap-4 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost">{t("app.cancel")}</button>
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="new-event-submit">
            {submitting ? "…" : t("app.eventCreate")}
          </button>
        </div>
      </form>
    </div>
  );
}
